import { useCallback, useRef, useState } from "react";
import { normalizeApiError } from "../api/error";
import { showErrorToast, showSuccessToast } from "../utils/toast";

export const useAsyncAction = (config = {}) => {
    const { initialLoading = false } = config;
    const [isLoading, setIsLoading] = useState(Boolean(initialLoading));
    const [error, setError] = useState(null);
    const pendingCountRef = useRef(0);

    const clearError = useCallback(() => setError(null), []);

    const run = useCallback(async (action, options = {}) => {
        const {
            errorFallback = "An error occurred. Please try again.",
            successMessage = "",
            silentErrorToast = false,
            silentSuccessToast = true,
        } = options;

        clearError();
        pendingCountRef.current += 1;
        setIsLoading(true);

        try {
            const result = await action();
            if (successMessage && !silentSuccessToast) {
                showSuccessToast(successMessage);
            }
            return result;
        } catch (err) {
            const parsedError = normalizeApiError(err, errorFallback);
            setError(parsedError);
            if (!silentErrorToast) {
                showErrorToast(parsedError.message);
            }
            return null;
        } finally {
            pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
            setIsLoading(pendingCountRef.current > 0);
        }
    }, [clearError]);

    return {
        isLoading,
        error,
        errorMessage: error?.message || "",
        setError,
        clearError,
        run,
    };
};
